import React, { useEffect, useState, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Button, message } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { Breadcrumbs } from '../../../components'
import UploadFormModal from '../../../forms/UploadFormModal'
import { selectAllBooks } from '../../../redux/features/books/bookSelector'
import { deleteBookThunk } from '../../../redux/features/books/booksThunk'
import { ViewContentModal } from '../../../components/ViewContentModal'
import { ContentTable } from '../../../components/ContentTable'
import { ContentFilterBar } from '../../../components/ContentFilterBar'
import { BulkActionsBar } from '../../../components/BulkActionsBar'

interface DataType {
  key: string
  id: string
  bookId: string
  title: string
  category: string
  description: string
  thumbnail: string
  url: string
  likes?: number
  views?: number
  createdAt?: string
}

const BooksPage = () => {
  const [isViewModalVisible, setIsViewModalVisible] = useState(false)
  const [isUploadFormModalVisible, setIsUploadFormModalVisible] = useState(false)
  const [viewContent, setViewContent] = useState<DataType | null>(null)
  const [editData, setEditData] = useState<any>(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [selectedRows, setSelectedRows] = useState<DataType[]>([])

  // Filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined)

  const books = useSelector(selectAllBooks)
  const dispatch = useDispatch<any>()

  // Transform book data
  const transformedBookData: DataType[] = useMemo(() => {
    if (!books || !Array.isArray(books)) return []
    return books.map((book) => ({
      key: book?.bookId || book?.id || 'unique-key-' + Math.random(), // Ensure unique key
      id: book?.id,
      bookId: book?.bookId,
      title: book?.title || '',
      category: book?.category || '',
      description: book?.description || '',
      thumbnail: book?.thumbnail || '',
      url: book?.url || '',
      likes: book?.likes || 0,
      views: (book as any)?.views || 0,
      createdAt: (book as any)?.createdAt,
    })) || []
  }, [books])

  // Get unique categories for filter
  const categories = useMemo(() => {
    const cats = transformedBookData
      .map((b) => b.category)
      .filter((c) => c && c.trim() !== '')
    return [...new Set(cats)]
  }, [transformedBookData])

  // Filtered data
  const filteredData = useMemo(() => {
    let result = transformedBookData

    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter(
        (item) =>
          item.title.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query)
      )
    }

    if (categoryFilter) {
      result = result.filter((item) => item.category === categoryFilter)
    }

    return result
  }, [transformedBookData, searchQuery, categoryFilter])

  const handleView = (record: DataType) => {
    setViewContent(record)
    setIsViewModalVisible(true)
  }

  const handleEdit = (record: DataType) => {
    setEditData({
      id: record.bookId,
      title: record.title,
      description: record.description,
      category: record.category,
      thumbnail: record.thumbnail,
      url: record.url,
    })
    setIsUploadFormModalVisible(true)
  }

  const handleDelete = (record: DataType) => {
    dispatch(deleteBookThunk(record.bookId))
    message.success('Book deleted successfully')
  }

  const handleBulkDelete = () => {
    selectedRows.forEach((record) => {
      dispatch(deleteBookThunk(record.bookId))
    })
    message.success(`${selectedRows.length} books deleted successfully`)
    setSelectedRowKeys([])
    setSelectedRows([])
  }

  const handleSelectionChange = (keys: React.Key[], records: DataType[]) => {
    setSelectedRowKeys(keys)
    setSelectedRows(records)
  }

  const handleViewModalClose = () => {
    setIsViewModalVisible(false)
    setViewContent(null)
  }

  const handleSwitchToEdit = () => {
    if (viewContent) {
      handleEdit(viewContent)
      handleViewModalClose()
    }
  }

  const showUploadFormModal = () => {
    setEditData(null)
    setIsUploadFormModalVisible(true)
  }

  const handleCancelUploadFormModal = () => {
    setIsUploadFormModalVisible(false)
    setEditData(null)
  }

  const handleClearFilters = () => {
    setSearchQuery('')
    setCategoryFilter(undefined)
  }

  return (
    <div>
      <Breadcrumbs />

      {/* Upload Button */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          marginBottom: '16px',
          background: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)',
          borderRadius: '10px',
          border: '1px solid #b7eb8f',
        }}
      >
        <div>
          <h3 style={{ margin: 0, color: '#389e0d' }}>Books</h3>
          <span style={{ color: '#666' }}>{filteredData.length} books</span>
        </div>
        <Button
          type="primary"
          icon={<UploadOutlined />}
          onClick={showUploadFormModal}
          style={{ background: '#389e0d', borderColor: '#389e0d' }}
        >
          Upload Book
        </Button>
      </div>

      {/* Filter Bar */}
      <ContentFilterBar
        onSearch={setSearchQuery}
        onCategoryChange={setCategoryFilter}
        onClear={handleClearFilters}
        searchValue={searchQuery}
        categoryValue={categoryFilter}
        categories={categories}
        placeholder="Search books..."
      />

      {/* Bulk Actions Bar */}
      <BulkActionsBar
        selectedCount={selectedRowKeys.length}
        onBulkDelete={handleBulkDelete}
        onClearSelection={() => {
          setSelectedRowKeys([])
          setSelectedRows([])
        }}
        itemType="book"
      />

      {/* Content Table */}
      <ContentTable
        dataSource={filteredData}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        selectedRowKeys={selectedRowKeys}
        onSelectionChange={handleSelectionChange}
        type="book"
      />

      {/* View Modal */}
      {viewContent && (
        <ViewContentModal
          isVisible={isViewModalVisible}
          onClose={handleViewModalClose}
          onEdit={handleSwitchToEdit}
          type="book"
          content={viewContent}
        />
      )}

      {/* Upload/Edit Modal */}
      <UploadFormModal
        visible={isUploadFormModalVisible}
        onClose={handleCancelUploadFormModal}
        label="book"
        editData={editData}
      />
    </div>
  )
}

export { BooksPage }
